import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { Card, Button } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import SystemChecksSection from './components/SystemChecksSection';
import ChecklistSection from './components/ChecklistSection';
import NotesSection from './components/NotesSection';
import ReadinessSummary from './components/ReadinessSummary';
import { isHasBacktestsCheck } from './utils/systemChecks';

type CheckStatus = 'passed' | 'failed' | 'warning' | 'not_applicable';
type OverallStatus = 'ready' | 'not_ready' | 'needs_attention';

const recomputeSystemChecksSummary = (checks: { status: CheckStatus }[]) => {
    let passed = 0;
    let failed = 0;
    let warning = 0;
    let notApplicable = 0;

    for (const check of checks) {
        switch (check.status) {
            case 'passed':
                passed += 1;
                break;
            case 'failed':
                failed += 1;
                break;
            case 'warning':
                warning += 1;
                break;
            case 'not_applicable':
                notApplicable += 1;
                break;
        }
    }

    const overallStatus: OverallStatus = failed > 0 ? 'not_ready' : warning > 0 ? 'needs_attention' : 'ready';

    return { passed, failed, warning, notApplicable, overallStatus };
};

const StrategyReadiness: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const strategyId = id ? parseInt(id, 10) : 0;

    // Fetch strategy details
    const strategyQuery = useAppQuery(
        {
            ...trpc.strategy.fetchStrategies.queryOptions({
                ids: strategyId ? [strategyId] : [],
                requireActive: false
            }),
            enabled: !!strategyId,
            staleTime: 120000
        },
        {
            fallbackErrorMessage: 'Failed to load strategy',
            showErrorToast: true
        }
    );

    // Fetch readiness data
    const readinessQuery = useAppQuery(
        {
            ...trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }),
            enabled: !!strategyId,
            staleTime: 30000
        },
        {
            fallbackErrorMessage: 'Failed to load readiness data',
            showErrorToast: true
        }
    );

    const coverageQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyCoverage.queryOptions({
                strategyId,
                entityType: null
            }),
            enabled: !!strategyId,
            staleTime: 30000
        },
        {
            fallbackErrorMessage: 'Failed to load backtest coverage',
            showErrorToast: true
        }
    );

    const scorecardsQuery = useAppQuery(
        {
            ...trpc.scorecards.list.queryOptions(),
            enabled: !!strategyId,
            staleTime: 120000
        },
        {
            fallbackErrorMessage: 'Failed to load scorecards',
            showErrorToast: true
        }
    );

    const strategy = useMemo(() => strategyQuery.data?.strategies?.[0], [strategyQuery.data]);

    const activeScorecard = useMemo(
        () => scorecardsQuery.data?.find((scorecard) => scorecard.id === strategy?.activeScorecardId) ?? null,
        [scorecardsQuery.data, strategy?.activeScorecardId]
    );

    const readinessData = readinessQuery.data;

    const systemChecksForDisplay = useMemo(() => {
        const raw = readinessData?.systemChecks;
        if (!raw) return null;

        const coverage = coverageQuery.data;
        if (!coverage) return raw;

        const hasStrategyBacktests = (coverage.entitiesWithBacktestsCount ?? 0) > 0 || (coverage.totalBacktests ?? 0) > 0;

        const checks = raw.checks.map((check) => {
            if (!isHasBacktestsCheck(check)) return check;

            const status: CheckStatus = hasStrategyBacktests ? 'passed' : 'failed';

            return {
                ...check,
                status,
                details: hasStrategyBacktests ? check.details : (check.details ?? 'No current-version strategy backtests found.')
            };
        });

        return {
            ...raw,
            checks,
            summary: recomputeSystemChecksSummary(checks)
        };
    }, [readinessData?.systemChecks, coverageQuery.data]);

    if (strategyQuery.isLoading || readinessQuery.isLoading) {
        return (
            <UserLayout title="Strategy Dashboard - Project 314">
                <DataLoadingSpinner />
            </UserLayout>
        );
    }

    if (!strategy) {
        return (
            <UserLayout title="Strategy Dashboard - Project 314">
                <Card>
                    <div className="text-center py-8">
                        <p className="text-zinc-400">Strategy not found</p>
                        <Button className="mt-4" to="/user/tradingStrategies" variant="outline-dark">
                            Back to Strategies
                        </Button>
                    </div>
                </Card>
            </UserLayout>
        );
    }

    return (
        <UserLayout title={`${strategy.name} - Dashboard - Project 314`}>
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
                <div>
                    <h3 className="text-xl font-semibold">
                        <Link className="text-blue-500 hover:text-blue-400" to="/user/tradingStrategies">
                            Strategies
                        </Link>
                        {' / '}
                        <Link className="text-blue-500 hover:text-blue-400" to={`/user/tradingStrategy/${strategyId}`}>
                            {strategy.name}
                        </Link>
                        {' / '}
                        Dashboard
                    </h3>
                    <p className="text-zinc-400 text-sm mt-1">System checks, checklist, notes, and quick links for this strategy</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" to={`/user/tradingStrategy/${strategyId}`} variant="outline-dark">
                        Edit Strategy
                    </Button>
                    <Button size="sm" to={`/user/tradingStrategy/${strategyId}?section=backtests`} variant="outline-dark">
                        Backtests
                    </Button>
                    <Button linkProps={{ state: { strategyId } }} size="sm" to="/tools/backtestParameterAnalysis" variant="outline-dark">
                        Parameter Combinations
                    </Button>
                    <Button size="sm" to={`/user/scorecards?strategyId=${strategyId}`} variant="outline-dark">
                        Scorecards
                    </Button>
                </div>
            </div>

            {/* Summary */}
            {systemChecksForDisplay && <ReadinessSummary data={systemChecksForDisplay} />}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {/* Left Column: System Checks */}
                <div className="space-y-6">
                    <SystemChecksSection
                        activeScorecard={activeScorecard}
                        checks={systemChecksForDisplay?.checks ?? []}
                        strategyId={strategyId}
                    />
                </div>

                {/* Right Column: Checklist + Notes */}
                <div className="space-y-6">
                    <ChecklistSection items={readinessData?.checklistItems ?? []} strategyId={strategyId} />
                    <NotesSection notes={readinessData?.notes ?? []} strategyId={strategyId} />
                </div>
            </div>
        </UserLayout>
    );
};

export default StrategyReadiness;
